import { useFormik } from 'formik';
import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import { AssetBalanceParams, Methods } from '~/api/finance/financeApiTypes';
import { Button } from '~/atomic/atom/Button';
import { Skeleton } from '~/atomic/atom/Skeleton';
import { Select } from '~/atomic/molecula/Select';
import {
  PageTitleStyle, TextBoldStyle, TextSmallStyle,
} from '~/atomic/Typography';
import { accountReplenishmentSelector } from '~/feature/finance/accountReplenishment/accountReplenishment.selector';
import { MAX_ACCOUNT_REPLENISHMENT_AMOUNT, MIN_ACCOUNT_REPLENISHMENT_AMOUNT } from '~/feature/finance/accountReplenishment/accountReplenishment.slice';
import { getMethodsThunk } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.data';
import { choosePaySelector } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.selector';
import { choosePayActions } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.slice';
import { MethodChoose, PaymentMethod } from '~/feature/finance/BuyingSteps/PaymentMethod';
import { useAppDispatch, useAppSelector } from '~/store';

export const AccountReplenishmentChooseAmount:FC<{
  registerAssetBalance: (props: AssetBalanceParams) => void
}> = ({ registerAssetBalance }) => {
  const dispatch = useAppDispatch();
  const { amount } = useAppSelector(accountReplenishmentSelector);
  const {
    methods, isLoading, isPayLoading,
  } = useAppSelector(choosePaySelector);

  const accountReplenishmenForm = useFormik({
    initialValues: {
      method: {} as MethodChoose,
      amount,
    },
    onSubmit: async (values) => {
      dispatch(choosePayActions.setIsPayLoading(true));
      await registerAssetBalance({
        methodPayment: values.method.type === Methods.savedBankCard
          ? Methods.bankCard
          : values.method.type,
        bankCardId: values.method.cardId,
        amount: values.amount,
      });
      dispatch(choosePayActions.setIsPayLoading(false));
    },
    validationSchema: Yup.object({
      method: Yup
        .object<{
        type: Methods;
        cardId: number;
      }>()
        .test('check required method', 'Выберите способ пополнения', (value) => {
          if (!value?.type) {
            return false;
          }
          return true;
        }),
      amount: Yup
        .number()
        .required()
        .min(
          MIN_ACCOUNT_REPLENISHMENT_AMOUNT,
          `Минимальная сумма пополнения — ${MIN_ACCOUNT_REPLENISHMENT_AMOUNT} ₽`,
        )
        .max(
          MAX_ACCOUNT_REPLENISHMENT_AMOUNT,
          `Максимальная сумма пополнения — ${MAX_ACCOUNT_REPLENISHMENT_AMOUNT} ₽`,
        ),
    }),
  });

  useEffect(() => {
    dispatch(getMethodsThunk());
  }, []);

  return (
    <SCAccountReplenishmentChooseAmount
      onSubmit={accountReplenishmenForm.handleSubmit}
    >
      <SCTitle>
        Пополнить счет
      </SCTitle>
      <SCSelect
        onBlur={accountReplenishmenForm.handleBlur}
        onChange={(value) => {
          accountReplenishmenForm.setFieldValue('amount', value);
        }}
        value={accountReplenishmenForm.values.amount}
        showArrow
        placeholder="Выберите жанр произведения"
        optionFilterProp="label"
        options={[
          { value: 200, label: '200 ₽' },
          { value: 500, label: '500 ₽' },
          { value: 1000, label: '1000 ₽' },
          { value: 2000, label: '2000 ₽' },
        ]}
      />
      <SCMethodsWrap>
        {isLoading ? (
          <>
            <SCMethod>
              <Skeleton active title={false} paragraph={{ rows: 1 }} />
            </SCMethod>
            <SCMethod>
              <Skeleton active title={false} paragraph={{ rows: 1 }} />
            </SCMethod>
          </>
        ) : (
          <>
            <SCMethodError>
              {accountReplenishmenForm.touched?.method
                ? accountReplenishmenForm.errors?.method as string : ''}
            </SCMethodError>
            {methods
              .filter((method) => method.type !== Methods.balance && method.type !== Methods.Tinkoff)
              .map((method) => (
                <PaymentMethod
                  method={method}
                  activeMethod={accountReplenishmenForm.values.method}
                  price={amount}
                  onChange={accountReplenishmenForm.handleChange}
                />
              ))}
          </>
        )}
      </SCMethodsWrap>
      <Button
        block
        type="primary"
        htmlType="submit"
        isLoading={isPayLoading}
        size="large"
      >
        Пополнить счет
      </Button>
    </SCAccountReplenishmentChooseAmount>
  );
};

const SCTitle = styled.h3`
  ${PageTitleStyle};
`;

const SCAccountReplenishmentChooseAmount = styled.form`
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  
  > *:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const SCMethodError = styled.span`
  ${TextSmallStyle};
  color: var(--error-color);
`;

const SCMethodsWrap = styled.div`
  width: 100%;
`;

const SCMethod = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  padding: 8px;

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const SCSelect = styled(Select)`
  width: 100%;
  max-width: 200px;
  min-height: 44px;

  .ant-select-selector {
    min-height: 44px !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-item {
    ${TextBoldStyle};
    color: var(--black-color);
    line-height: 44px !important;
  }
`;
